import { Button } from "@mui/material";
import { FileProvider, useFileContext } from "context/FilesV2";
import { useLoading } from "hooks/useLoading";
import ConfirmDialog from "qubCommon/Dialogs/ConfirmationDialog";
import FilePreviewer from "qubCommon/FilePreviewer";
import { useRef, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { deleteBundle, deleteFile, downloadFile, uploadFiles } from "util";
import "./PanelladikesApplicationDetails.css";
import AlertBanner from "./Components/AlertBanner";
import Amendment from "./Components/Amendment";
import ApplicationActions from "./Components/ApplicationActions";
import ApplicationAttachments from "./Components/ApplicationAttachments";
import ChildAttachments from "../ApplicationDetails/Components/ChildAttachments";
import Children from "./Components/Children";
import Fathers from "./Components/Fathers";
import History from "./Components/History";
import ListFileItem from "./Components/ListFileItem";
import MothersInfo from "./Components/MothersInfo";
import SummaryCard from "./Components/SummaryCard";
import usePanelladikesApplicationDetails from "./usePanelladikesApplicationDetails";
import ChildrenIbans from "./Components/ChildrenIbans";
import { updateApplication } from "apis/controllers/student-applications";
import CopyDataDialog from "./Components/CopyDataDialog";
import ChangeChildrenIbansDialog from "./Components/ChangeChildrenIbansDialog";
import CrossApplicationMembershipWarnings from "../ApplicationDetails/Components/CrossApplicationMembershipWarnings";
import PreApprovalDialog from "./Components/PreApprovalDialog";
import {
    canReuseApplicantAmkaForSuccessfulStudent,
    isSchoolStudentCategory,
    isSuccessfulStudentCategory,
} from "./studentApplication";

const url = import.meta.env.VITE_REACT_APP_BASE_URL;
const hasValue = (value) => value !== undefined && value !== null && value !== "";
const isValidMobilePhone = (value) => /^69\d{8}$/.test(String(value || "").trim());

const isEmailVerificationError = (message) => {
    const text = String(message || "").toLocaleLowerCase("el-GR");
    return text.includes("email") && text.includes("\u03B5\u03C0\u03B9\u03B2\u03B5\u03B2");
};

const findLookupById = (options, id) =>
    options?.find((option) => String(option?.id) === String(id));


const PanelladikesApplicationDetails = () => {
    const {
        form,
        data,
        succeeded,
        files,
        fetchApplication,
        resetForm,
        openCopyDialog,
        setOpenCopyDialog,
        confirmCopy
    } = usePanelladikesApplicationDetails();



    const { id } = useParams();

    if (succeeded)

        return (
            <div className="applications-container">
                <FileProvider
                    downloadFile={(fileId, type) => downloadFile(fileId, type)}
                    uploadFiles={(fileGroups) => uploadFiles(fileGroups, null)}
                    onDeleteBundle={async (bundleId) =>
                        await deleteBundle(bundleId, 4, null, id, null)
                    }
                    onDeleteFile={async (fileId) => {
                        await deleteFile(fileId, 4, null, id, null);
                    }}
                >
                    <Form
                        form={form}
                        data={data}
                        files={files}
                        fetchApplication={fetchApplication}
                        resetForm={resetForm}
                    />
                    <CopyDataDialog
                        open={openCopyDialog}
                        onClose={() => setOpenCopyDialog(false)}
                        onConfirm={confirmCopy}
                        prevApp={data?.application?.copyData}
                    />

                </FileProvider>
            </div>
        );
};

const Form = (props) => {


    const { form, data, files, fetchApplication, resetForm } =
        props;
    const [openAmendment, setOpenAmendment] = useState(false);
    const [openChangeIban, setOpenChangeIban] = useState(false);
    const [openPreApproval, setOpenPreApproval] = useState(false);
    const [fileToPreview, setFileToPreview] = useState(null);
    const [errorBanner, setErrorBanner] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogProps, setDialogProps] = useState({
        title: "",
        content: "",
        onConfirm: () => { },
        extraButtons: [],
    });
    const { id } = useParams();
    const { fileGroups, control } = useFileContext();
    const childrenFileRefs = useRef([]);
    const applicationFileRef = useRef();

    const { setLoading } = useLoading();
    const settings = data?.application?.settings;
    const [emailVerificationRequired, setEmailVerificationRequired] =
        useState(false);
    const shouldShowEmailVerification =
        settings?.shouldSendEmailVerification === true;
    const isLoadedApplicantEmailVerified =
        settings?.shouldSendEmailVerification === false;
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const applicantMunicipalityId = form.watch("applicant.address.municipality.id");
    const applicantMunicipality =
        findLookupById(data?.lookups?.municipalities, applicantMunicipalityId) ||
        data?.application?.applicant?.address?.municipality;

    const getApplicationFormValues = () => {
        const { applicantEmailVerification, childSchoolSelections, ...values } =
            form.getValues();

        const municipalityId = values?.applicant?.address?.municipality?.id;
        const municipality = findLookupById(
            data?.lookups?.municipalities,
            municipalityId
        );
        const normalizedMunicipality = hasValue(municipalityId)
            ? {
                id: municipality?.id ?? municipalityId,
                name:
                    municipality?.name ??
                    values.applicant.address.municipality?.name ??
                    null,
            }
            : null;

        return values?.applicant?.address
            ? {
                ...values,
                applicant: {
                    ...values.applicant,
                    address: {
                        ...values.applicant.address,
                        municipality: normalizedMunicipality,
                    },
                },
            }
            : values;
    };

    const isApplicantEmailVerified = () => {
        const applicant = form.getValues("applicant");
        const verification = form.getValues("applicantEmailVerification");

        return (
            hasValue(applicant?.email) &&
            verification?.isVerified === true &&
            verification?.verifiedEmail === applicant.email
        );
    };

    const shouldRequireApplicantEmailVerification = () =>
        shouldShowEmailVerification ||
        isLoadedApplicantEmailVerified ||
        emailVerificationRequired;


    const handleValidate = ({
        requireEmailVerification = false,
        forSubmit = false,
    } = {}) => {
        const applicant = form.getValues("applicant");
        const husbands = form.getValues("husbands");
        const children = form.getValues("children");
        const childrenHelp = form.getValues("childrenHelp") || [];
        const childrenIbans = form.getValues('childrenIbans');

        const errors = [];

        try {
            const hasSameAfmChild = childrenIbans?.some((childIban) => {
                const child = childrenHelp.find(
                    (item) => String(item?.id) === String(childIban?.childId)
                );
                const canReuseApplicant =
                    canReuseApplicantAmkaForSuccessfulStudent({
                        applicantAmka: applicant?.amka,
                        childAmka: child?.amka,
                        category: child?.category,
                        applicantIsOrphanInSeasonWhitelist:
                            data?.application?.applicantIsOrphanInSeasonWhitelist,
                    });

                return childIban?.afm === applicant?.afm && !canReuseApplicant;
            });

            if (hasSameAfmChild && forSubmit) {
                errors.push(`Βρέθηκε τέκνο με το ίδιο ΑΦΜ (${applicant.afm}) με τον δικαιούχο.`);
            }
        }
        catch (e) {
            console.log(e)
        }
        if (applicant?.phone1?.length > 20)
            errors.push("Το τηλέφωνο 1 δεν μπορεί να ξεπερνά τους 20 χαρακτήρες.");
        if (applicant?.phone2?.length > 20)
            errors.push("Το τηλέφωνο 2 δεν μπορεί να ξεπερνά τους 20 χαρακτήρες.");
        if (applicant?.mobilePhone && !isValidMobilePhone(applicant.mobilePhone))
            errors.push("Το κινητό τηλέφωνο πρέπει να ξεκινά από 69 και να αποτελείται από 10 ψηφία.");
        if (applicant?.city?.length > 50)
            errors.push("Η πόλη δεν μπορεί να ξεπερνά τους 50 χαρακτήρες.");
        if (applicant?.addressLine?.length > 100)
            errors.push("Η διεύθυνση δεν μπορεί να ξεπερνά τους 100 χαρακτήρες.");
        if (applicant?.postalCode?.length > 5)
            errors.push(
                "Ο ταχυδρομικός κώδικας δεν μπορεί να ξεπερνά τους 5 χαρακτήρες."
            );
        if (applicant?.email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(applicant.email)) {
                errors.push("Το email του δικαιούχου δεν είναι έγκυρο.");
            }

        } else {
            errors.push("Το email του δικαιούχου είναι υποχρεωτικό.");
        }

        if (
            requireEmailVerification &&
            shouldRequireApplicantEmailVerification() &&
            !isApplicantEmailVerified()
        ) {
            errors.push("Παρακαλώ επιβεβαιώστε το email του δικαιούχου πριν την υποβολή.");
        }

        // Husband fields (created/edited)
        if (husbands?.created || husbands?.updated) {
            const allHusbands = [
                ...(husbands.created || []),
                ...(husbands.updated || []),
            ];
            allHusbands.forEach((h, index) => {
                if (h.amka?.length > 11)
                    errors.push(`Βρέθηκε ΑΜΚΑ γονέα που ξεπερνά τους 11 χαρακτήρες.`);
                if (h.ama?.length > 12)
                    errors.push(`Βρέθηκε ΑΜΑ γονέα που ξεπερνά τους 12 χαρακτήρες.`);
                if (h.firstName?.length > 50)
                    errors.push(`Βρέθηκε Όνομα γονέα που ξεπερνά τους 50 χαρακτήρες.`);
                if (h.lastName?.length > 50)
                    errors.push(`Βρέθηκε Επώνυμο γονέα που ξεπερνά τους 50 χαρακτήρες.`);
            });
        }

        // Children fields (created/edited)
        if (children?.created || children?.updated) {
            const allChildren = [
                ...(children.created || []),
                ...(children.updated || []),
            ];
            allChildren.forEach((c, index) => {
                if (c.amka?.length > 11)
                    errors.push(`Βρέθηκε ΑΜΚΑ τέκνου που ξεπερνά τους 11 χαρακτήρες.`);
                if (c.firstName?.length > 50)
                    errors.push(`Βρέθηκε Όνομα τέκνου που ξεπερνά τους 50 χαρακτήρες.`);
                if (c.lastName?.length > 50)
                    errors.push(`Βρέθηκε Επώνυμο τέκνου που ξεπερνά τους 50 χαρακτήρες.`);
            });
        }
        if (forSubmit) {
            childrenHelp.forEach((child, index) => {
                if (!isSchoolStudentCategory(child?.category)) return;

                const schoolSelection = childrenFileRefs.current[index]?.filebundles || child;
                const childLabel = `${child?.lastName ?? ""} ${child?.firstName ?? ""}`.trim();
                const prefix = childLabel
                    ? `Παιδί ${childLabel}:`
                    : `Παιδί ${index + 1}:`;

                if (!hasValue(schoolSelection?.schoolGeo?.id))
                    errors.push(`${prefix} απαιτείται Δήμος.`);
                if (!hasValue(schoolSelection?.schoolType?.id))
                    errors.push(`${prefix} απαιτείται Τύπος Σχολείου.`);
                if (!hasValue(schoolSelection?.school?.id))
                    errors.push(`${prefix} απαιτείται Σχολείο.`);
                if (!hasValue(schoolSelection?.schoolClass?.id))
                    errors.push(`${prefix} απαιτείται Τάξη.`);
                if (!hasValue(schoolSelection?.schoolRegNumber))
                    errors.push(`${prefix} απαιτείται Α.Μ. Μαθητή.`);
            });
        }

        return errors;
    };

    const formatBundles = () => { };

    const getChildrenFiles = async () => {
        if (childrenFileRefs.current) {
            await Promise.all(
                childrenFileRefs.current.map(async (childRef) => {
                    if (
                        childRef &&
                        childRef.filebundles?.files
                            ?.filter((file) => file?.id)
                            ?.map((file) => file.id)?.length > 0
                    ) {
                        await childRef.uploadFiles();
                    }
                })
            );
        }
        const childrenFiles = [];
        childrenFileRefs.current.forEach((childRef) => {
            if (childRef) {
                const files =
                    childRef.filebundles?.files
                        ?.filter((file) => file?.id)
                        ?.map((file) => file.id) || [];
                const hasSchoolSelectionPayload =
                    childRef.filebundles?.hasSchoolSelectionPayload;

                if (files.length > 0 || hasSchoolSelectionPayload) {
                    childrenFiles.push({
                        childId: childRef.filebundles.childId,
                        bundles: files,
                        hasSchoolSelectionPayload,
                        ...(hasSchoolSelectionPayload
                            ? {
                                schoolGeo: childRef.filebundles.schoolGeo,
                                schoolType: childRef.filebundles.schoolType,
                                school: childRef.filebundles.school,
                                schoolClass: childRef.filebundles.schoolClass,
                                schoolRegNumber: childRef.filebundles.schoolRegNumber,
                            }
                            : {}),
                    });
                }
            }
        });

        return childrenFiles;
    };

    const getApplicationFiles = async () => {
        let applicationFiles;

        await applicationFileRef.current.uploadFiles();

        if (applicationFileRef) {
            // childRef.saveData();
            if (
                applicationFileRef.current?.filebundles?.files
                    ?.filter((file) => file?.id)
                    ?.map((file) => file.id)?.length > 0
            )
                applicationFiles =
                    applicationFileRef.current?.filebundles?.files
                        ?.filter((file) => file?.id)
                        ?.map((file) => file.id) || [];
        }
        return applicationFiles;
    };

    const formatDataWithoutBundles = async () => {
        return getApplicationFormValues();
    };

    const formatDataWithBundles = async () => {
        const data = getApplicationFormValues();
        const childrenFiles = await getChildrenFiles();
        const applicationFiles = await getApplicationFiles();

        const attachBundles = (childrenArray) => {
            return childrenArray.map((child) => {
                const fileMatch = childrenFiles.find(
                    (file) => file.childId === child.id
                );
                const childWithBundles = {
                    ...child,
                    bundles: fileMatch ? fileMatch.bundles : [],
                };

                if (fileMatch?.hasSchoolSelectionPayload) {
                    childWithBundles.schoolGeo = fileMatch.schoolGeo;
                    childWithBundles.schoolType = fileMatch.schoolType;
                    childWithBundles.school = fileMatch.school;
                    childWithBundles.schoolClass = fileMatch.schoolClass;
                    childWithBundles.schoolRegNumber = fileMatch.schoolRegNumber;
                }

                return childWithBundles;
            });
        };

        // Attach bundles to all children
        const allChildrenWithBundles = attachBundles(data.childrenHelp);

        // Get ids from form state
        const createdIds = data.children.created.map((c) => c.id);
        const updatedIds = data.children.updated.map((c) => c.id);

        // Build created from bundles-attached objects
        const createdChildren = allChildrenWithBundles.filter((child) =>
            createdIds.includes(child.id)
        );

        // Build updated from bundles-attached objects
        const updatedChildrenFromForm = allChildrenWithBundles.filter((child) =>
            updatedIds.includes(child.id)
        );

        // Detect children that got bundles or a school selection but are not yet marked updated
        const childrenWithAttachmentChanges = allChildrenWithBundles.filter(
            (child) => {
                const fileMatch = childrenFiles.find(
                    (file) => file.childId === child.id
                );
                return (
                    (child.bundles && child.bundles.length > 0) ||
                    fileMatch?.hasSchoolSelectionPayload
                );
            }
        );
        const newlyUpdatedChildren = childrenWithAttachmentChanges.filter(
            (child) =>
                !updatedIds.includes(child.id) && !createdIds.includes(child.id)
        );

        // Final updated = union of form updated + newly updated (from bundles)
        const updatedChildren = [
            ...updatedChildrenFromForm,
            ...newlyUpdatedChildren,
        ];

        const mergedValues = {
            ...data,
            bundles: applicationFiles || [],
            children: {
                created: createdChildren,
                updated: updatedChildren,
                deleted: data?.children?.deleted || [],
            },
        };

        return mergedValues;
    };

    const handleApplicationUpdate = async (
        action,
        requestJson,
        forceSave = false,
        navigateBack = false,
        forceGuardianInsuranceWarning = false
    ) => {
        const requireApplicantEmailVerification = () => {
            setEmailVerificationRequired(true);
            form.setValue("applicantEmailVerification.verifiedEmail", "", {
                shouldDirty: false,
                shouldValidate: false,
            });
            form.setValue("applicantEmailVerification.isVerified", false, {
                shouldDirty: false,
                shouldValidate: false,
            });
            form.setValue("applicantEmailVerification.code", "", {
                shouldDirty: false,
                shouldValidate: false,
            });
            form.setValue("applicantEmailVerification.codeSentEmail", "", {
                shouldDirty: false,
                shouldValidate: false,
            });
        };

        try {
            setLoading(true);

            if (requestJson?.reviews?.applicant === null)
                requestJson.reviews.applicant = {};
            if (requestJson?.reviews?.childrenIbans === null)
                requestJson.reviews.childrenIbans = {};
            if (requestJson?.reviews?.husbands === null)
                requestJson.reviews.husbands = {};
            if (requestJson?.reviews?.children === null)
                requestJson.reviews.children = {};
            const res = await updateApplication({
                ...requestJson,
                id,
                action,
                forceSave,
                forceGuardianInsuranceWarning,
            });

            if (res?.status === "success") {
                setEmailVerificationRequired(false);
                queryClient.removeQueries(["application-files", id]);
                queryClient.removeQueries(["files"]);
                if (navigateBack) {
                    try {
                        navigate(-1);
                    } catch {
                        navigate("/applications-navigate");
                    }
                }
                await fetchApplication();

                setOpenDialog(false);
            }
            if (res?.status === "warning") {
                if (requestJson?.children) {
                    requestJson.children.created = [];
                    requestJson.children.updated = [];
                    requestJson.children.deleted = [];
                }
                if (requestJson?.husbands) {
                    requestJson.husbands.created = [];
                    requestJson.husbands.updated = [];
                    requestJson.husbands.deleted = [];
                }
                setOpenDialog(false);
                setDialogProps({
                    title: "Υποβολή Αίτησης",
                    content: res?.message,
                    extraButtons: res?.data?.actions?.map((warningAction) => ({
                        action: warningAction?.action,
                        txt: warningAction?.txt,
                        onAction: async () => {
                            const isGuardianInsuranceWarning =
                                res?.data?.warningCode === "guardianInsurance";
                            const isForApprovalIncompleteReviewsWarning =
                                res?.data?.warningCode === "forApprovalIncompleteReviews";
                            const hasAcceptedGuardianInsuranceWarning =
                                forceGuardianInsuranceWarning ||
                                (isGuardianInsuranceWarning &&
                                    warningAction?.action === "submit");
                            const nextRequestJson = isForApprovalIncompleteReviewsWarning
                                ? await formatDataWithBundles()
                                : requestJson;

                            if (isForApprovalIncompleteReviewsWarning && requestJson?.comments) {
                                nextRequestJson.comments = requestJson.comments;
                            }

                            await handleApplicationUpdate(
                                warningAction?.action,
                                nextRequestJson,
                                isGuardianInsuranceWarning ? false : true,
                                false,
                                hasAcceptedGuardianInsuranceWarning
                            );
                        },
                    })),
                    onConfirm: () => { },
                });
                setOpenDialog(true);
                // setOpenDialog(false);
                //await fetchApplication();
            }
            if (res?.status === "error") {
                setErrorBanner(res?.message);
                if (action === "submit") {
                    // resetForm clears this client-only state; restore it only
                    // when the application reload keeps the exact same email.
                    const emailVerification = form.getValues(
                        "applicantEmailVerification"
                    );

                    await resetForm();

                    const reloadedApplicantEmail = form.getValues("applicant.email");
                    const canRestoreEmailVerification =
                        emailVerification?.isVerified === true &&
                        emailVerification?.verifiedEmail === reloadedApplicantEmail &&
                        !isEmailVerificationError(res?.message);

                    if (canRestoreEmailVerification) {
                        form.setValue(
                            "applicantEmailVerification.verifiedEmail",
                            emailVerification.verifiedEmail,
                            { shouldDirty: false, shouldValidate: false }
                        );
                        form.setValue(
                            "applicantEmailVerification.isVerified",
                            true,
                            { shouldDirty: false, shouldValidate: false }
                        );
                    }
                }
                if (action === "submit" && isEmailVerificationError(res?.message)) {
                    requireApplicantEmailVerification();
                }
                setOpenDialog(false);
            }

            setLoading(false);
            return res;
        } catch (e) {
            console.log(e);
            setLoading(false);
            setErrorBanner("<div>Προέκυψε Σφάλμα!</div>");
            setOpenDialog(false);
            return null;
        }
    };

    const handleOpenDialog = (actionType) => {
        switch (actionType) {
            case "accept":
                setDialogProps({
                    title: "Αποδοχή Τροποποιήσεων",
                    content: "Θέλετε σίγουρα να αποδεχτείτε τις τροποποιήσεις;",
                    onConfirm: handleAccept,
                });
                break;
            case "refuse":
                setDialogProps({
                    title: "Απόρριψη Τροποποιήσεων",
                    content: "Θέλετε σίγουρα να απορρίψετε τις τροποποιήσεις;",
                    onConfirm: handleRefuse,
                });
                break;
            case "approve":
                setDialogProps({
                    title: "Έγκριση Αίτησης",
                    content: "Θέλετε σίγουρα να εγκρίνετε την αίτηση;",
                    onConfirm: handleApprove,
                    commentsOn: settings?.approve?.commentsOn,
                    withFileUpload: settings?.approve?.withFileUpload,
                    fileDatakey: settings?.approve?.fileDataKey,
                    fileDataValue: settings?.approve?.fileDataValue,
                });
                break;
            case "forApproval":
                setDialogProps({
                    title: "Προς Έγκριση",
                    content: "Θέλετε να στείλετε την αίτηση προς έγκριση από την υπηρεσία;",
                    onConfirm: handleForApproval,
                    commentsOn: settings?.forApproval?.commentsOn,
                    withFileUpload: settings?.forApproval?.withFileUpload,
                    fileDatakey: settings?.forApproval?.fileDataKey,
                    fileDataValue: settings?.forApproval?.fileDataValue,
                });
                break;
            case "forResubmit":
                setDialogProps({
                    title: "Επιστροφή στον Δικαιούχο",
                    content: "Θέλετε σίγουρα να επιστρέψετε την αίτηση στον δικαιούχο;",
                    onConfirm: handleForResubmit,
                    commentsOn: settings?.forResubmit?.commentsOn,
                    withFileUpload: settings?.forResubmit?.withFileUpload,
                    fileDatakey: settings?.forResubmit?.fileDataKey,
                    fileDataValue: settings?.forResubmit?.fileDataValue,
                });
                break;
            case "reject":
                setDialogProps({
                    title: "Απόρριψη Αίτησης",
                    content: ` Θέλετε σίγουρα να επιστρέψετε την αίτηση στον δικαιούχο; 
            Παρακαλώ αναφέρετε τους λόγους απόρριψης.`,
                    onConfirm: settings?.reject?.withFileUpload
                        ? handleRejectWithFileUpload
                        : handleReject,
                    commentsOn: true,
                    withFileUpload: settings?.reject?.withFileUpload,
                    fileDatakey: settings?.reject?.fileDataKey,
                    fileDataValue: settings?.reject?.fileDataValue,
                });
                break;
            case "submit":
                setDialogProps({
                    title: "Υποβολή Αίτησης",
                    content:
                        "<strong>ΠΡΟΣΟΧΗ!</strong><br/> Μετά την Υποβολή της αίτησης δεν επιτρέπεται μεταβολή σε κανένα στοιχείο της! Βεβαιωθείτε για τα στοιχεία της αίτησής σας.",
                    onConfirm: handleSubmit,
                    withFileUpload: settings?.submit?.withFileUpload,
                    fileDatakey: settings?.submit?.fileDataKey,
                    fileDataValue: settings?.submit?.fileDataValue,
                });
                break;
            case "cancel":
                setDialogProps({
                    title: "Διαγραφή / Υπαναχώρηση",
                    content:
                        "Θέλετε σίγουρα να προχωρήσετε σε Διαγραφή / Υπαναχώρηση της αίτησης;",
                    onConfirm: handleCancel,
                });
                break;
            case "backToHome":
                const dirtyFields = Object.keys(form.formState.dirtyFields).map(
                    (x) => ({
                        field: x,
                        value: form.getValues(x),
                    })
                );
                if (!!dirtyFields?.length || control.isDirty)
                    setDialogProps({
                        title: "Προσοχή!",
                        content:
                            "Έχετε πραγματοποιήσει αλλαγές στη φόρμα σας. Αν αποχωρήσετε οι αλλαγές θα χαθούν. Θέλετε να κλείσετε τη σελίδα;",
                        onConfirm: () => navigate("/applications-navigate"),
                    });
                else navigate("/applications-navigate");
                break;
            case "partiallyApprove":
                setDialogProps({
                    title: settings?.partiallyApprove?.partiallyApproveBtnTxt,
                    content:
                        "Θέλετε σίγουρα να προχωρήσετε σε αλλαγή κατάστασης της αίτησης;",
                    onConfirm: handlePartiallyApprove,
                    withFileUpload: settings?.partiallyApprove?.withFileUpload,
                    fileDatakey: settings?.partiallyApprove?.fileDataKey,
                    fileDataValue: settings?.partiallyApprove?.fileDataValue,
                });
                break;
            default:
                return;
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = async () => {
        await setOpenDialog(false);
        await setDialogProps((prev) => ({
            ...prev,
            withFileUpload: false,
            extraButtons: [],
        }));
    };

    const handlePreviewFile = async (file) => {
        const res = await downloadFile(file?.id, file?.type ?? file?.contentType);
        setFileToPreview(res);
    };

    // Refactored handlers

    const handleChangeIbansSave = async (newIbans) => {
        const requestJson = await formatDataWithoutBundles();
        console.log(newIbans)
        const newIbansById = new Map(
            newIbans.map((n) => [n.childId, n])
        );
        console.log(newIbansById)

        requestJson.childrenIbans = requestJson.childrenIbans.map((child) => {
            const replacement = newIbansById.get(child.childId);
            console.log(replacement)
            if (!replacement) return child;

            return {
                ...child,
                iban: replacement.iban,
                afm: replacement.afm,
            };
        });

        const res = await handleApplicationUpdate(
            "change_iban",
            requestJson,
        );

        // 🔜 submit requestJson to backend
    };




    const handleSave = async () => {
        try {
            const requestJson = await formatDataWithBundles();
            // const { bundleIds } = formatBundles();
            await handleApplicationUpdate("save", requestJson);
        } catch {
            setLoading(false);
            setErrorBanner("<div>Προέκυψε σφάλμα!</div>");
        }
    };

    const handleAccept = async () => {
        await handleApplicationUpdate(
            "accept",
            { id: form.getValues("id") },
            false
        );
    };

    const handleRefuse = async () => {
        await handleApplicationUpdate(
            "refuse",
            { id: form.getValues("id") },
            false
        );
    };

    const handleApproveSave = async () => {
        try {
            const requestJson = await formatDataWithBundles();
            // const { bundleIds } = formatBundles();
            await handleApplicationUpdate("save", requestJson);

            // setLoading(true);

            // ToDo SOS Uncomment this
            // const resFiles = await control.uploadFileGroups();
            // if (resFiles?.status === "error") {
            //   setErrorBanner(resFiles?.message);
            //   setLoading(false);
            //   return;
            // }
            // await fetchApplication();

            // setLoading(false);
        } catch {
            setLoading(false);
            setErrorBanner("<div>Προέκυψε σφάλμα!</div>");
        }
    };

    const handleApprove = async (memberDecisions) => {
        const requestJson = await formatDataWithBundles();
        requestJson.memberDecisions = memberDecisions;
        await handleApplicationUpdate("approve", requestJson, false, true);
        setOpenPreApproval(false);
    };

    const handleForApproval = async (comments) => {
        const requestJson = await formatDataWithBundles();
        if (comments) requestJson.comments = comments;
        await handleApplicationUpdate("for_approval", requestJson);
    };

    const handleForResubmit = async (comments) => {
        const requestJson = await formatDataWithBundles();
        if (comments) requestJson.comments = comments;
        await handleApplicationUpdate("for_resubmit", requestJson, false, true);
    };

    const handleRejectWithFileUpload = async (comments) => {
        const { bundleIds } = formatBundles();
        const additionalData =
            fileBundles?.length > 0
                ? {
                    id: form.getValues("id"),
                    action: "reject",
                    comments,
                    bundles: fileBundles,
                }
                : {
                    id: form.getValues("id"),
                    action: "reject",
                    comments,
                };
        await handleApplicationUpdate("reject", additionalData, false);
    };

    const handleReject = async (comments) => {
        const requestJson = await formatDataWithBundles();
        if (comments) requestJson.comments = comments;
        await handleApplicationUpdate("reject", requestJson, false, true);
    };

    const handleSubmit = async (comments) => {
        const requestJson = await formatDataWithBundles();
        if (comments) requestJson.comments = comments;
        await handleApplicationUpdate("submit", requestJson);
    };

    const handleCancel = async (comments) => {
        const requestJson = await formatDataWithoutBundles();
        if (comments) requestJson.comments = comments;
        await handleApplicationUpdate("cancel", requestJson);
    };

    const handlePartiallyApprove = async (fileBundles = []) => {
        await handleApplicationUpdate(
            "partially_approve",
            fileBundles?.length > 0
                ? {
                    bundles: fileBundles,
                }
                : {},
            false
        );
    };


    return (
        <FormProvider {...form}>
            <FilePreviewer
                file={fileToPreview}
                onClose={() => setFileToPreview(null)}
            />
            <ConfirmDialog
                open={openDialog}
                title={dialogProps.title}
                content={dialogProps.content}
                onClose={handleCloseDialog}
                onConfirm={dialogProps.onConfirm}
                commentsOn={dialogProps.commentsOn}
                withFileUpload={dialogProps?.withFileUpload}
                fileDatakey={dialogProps?.fileDatakey}
                fileDataValue={dialogProps?.fileDataValue}
                extraButtons={dialogProps?.extraButtons}
            />
            <PreApprovalDialog
                open={openPreApproval}
                children={data?.application?.children ?? []}
                reasons={data?.lookups?.directApprovalReasons ?? []}
                onClose={() => setOpenPreApproval(false)}
                onConfirm={handleApprove}
            />
            <CrossApplicationMembershipWarnings
                warnings={data?.warnings?.crossApplicationMemberships}
                applicationRoute="student-application"
            />
            <div>
                <AlertBanner canClose={false} info={data?.application?.status}>
                    <div style={{ marginTop: 4, marginBottom: 4 }}>
                        {data?.application?.status?.files?.length > 0 &&
                            data?.application?.status?.files.map((file, index) => (
                                <ListFileItem
                                    key={index}
                                    file={file}
                                    index={index}
                                    handlePreviewFile={() => handlePreviewFile(file)}
                                />
                            ))}
                    </div>

                    <div className="buttons-container">
                        {data?.application?.status?.refuseBtnText && (
                            <Button
                                variant="contained"
                                color="error"
                                onClick={() => handleOpenDialog("refuse")}
                            >
                                {data?.application?.status?.refuseBtnText}
                            </Button>
                        )}
                        {data?.application?.status?.acceptBtnText && (
                            <Button
                                variant="contained"
                                onClick={() => handleOpenDialog("accept")}
                            >
                                {data?.application?.status?.acceptBtnText}
                            </Button>
                        )}
                    </div>
                </AlertBanner>
                <div
                    style={{
                        display: "flex",
                        width: "100%",
                        marginLeft: "auto",
                        marginTop: "2px",
                    }}
                >
                    <a
                        style={{ marginLeft: "auto" }}
                        className=" ds-link small-font-size "
                        href={`${url}/documents/ΛΑΕ-ΟΠΕΚΑ Επιτυχόντες-ούσες Πανελληνίων - Οδηγίες Χρήσης για την Υποβολή Αίτησης 2026.pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Βοήθεια / Οδηγίες Χρήσης
                    </a>
                </div>
                <div
                    style={{
                        display: "flex",
                        width: "100%",
                        marginLeft: "auto",
                        marginTop: "4px",
                    }}
                >
                    <a
                        style={{ marginLeft: "auto" }}
                        className=" ds-link small-font-size "
                        href="mailto:lae-miteres@opeka.gr"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Επικοινωνήστε με e-mail με την Υπηρεσία ΛΑΕ ΟΠΕΚΑ
                    </a>
                </div>
            </div>
            <SummaryCard
                protocolNumber={data?.application?.protocolNumber}
                protocolNumber2={data?.application?.protocolNumber2}
                status={data?.application?.status}
                type={data?.application?.type}
                created={data?.application?.created}
                updated={data?.application?.updated}
                submitted={data?.application?.submitted}
                season={data?.application?.season?.name}
            />
            <MothersInfo
                applicant={data?.application?.applicant}
                review={data?.application?.reviews?.applicant}
                canEdit={settings?.applicant?.canEdit}
                canApprove={settings?.applicant?.canApprove}
                canPartiallyEdit={settings?.applicant?.canPartiallyEdit}
                canViewReview={settings?.applicant?.canViewReview}
                canViewWSResult={settings?.applicant?.canViewWSResult}
                useLegacyPhoneFields={false}
                isLoadedEmailVerified={isLoadedApplicantEmailVerified}
                shouldShowEmailVerification={shouldShowEmailVerification}
                forceEmailVerification={emailVerificationRequired}
                onEmailVerified={() => setEmailVerificationRequired(false)}
            />

            {/* <FathersTable /> */}
            <Fathers
                husbands={data?.application?.husbands}
                review={data?.application?.reviews?.husbands}
                settings={settings?.husbands}
            />
            <Children
                children={data?.application?.children}
                settings={settings?.children}
                review={data?.application?.reviews?.children}
                programYear={data?.application?.season?.id}
                applicantIsOrphanInSeasonWhitelist={data?.application?.applicantIsOrphanInSeasonWhitelist}
            />
            {/* ✅ Show BeneficiariesIban if any child has category.id === 2 */}
            {form.watch("childrenHelp")?.length > 0 && form.watch("childrenHelp")?.some(
                (child) => isSuccessfulStudentCategory(child?.category)
            ) && <ChildrenIbans canEdit={settings?.childrenIbans?.canEdit}
                review={data?.application?.reviews?.childrenIbans}
                settings={settings?.childrenIbans} />}

            <ApplicationAttachments
                ref={(el) => (applicationFileRef.current = el)}
                settings={data?.application?.settings?.attachments}
            />
            {form.watch("childrenHelp")?.length > 0 &&
                form.watch("childrenHelp")?.map((child, index) => {
                    if (child?.category?.fileDataKey && child?.category?.fileDataValue) {
                        return (
                            <ChildAttachments
                                key={child?.id ?? index}
                                child={child}
                                dataKey={child?.category?.fileDataKey}
                                dataValue={child?.category?.fileDataValue}
                                season={data?.application?.season}
                                lookups={data?.lookups}
                                applicantMunicipality={applicantMunicipality}
                                formIndex={index}
                                showSchoolSelector={isSchoolStudentCategory(child?.category)}
                                canEditSchool={settings?.canEdit}
                                ref={(el) => (childrenFileRefs.current[index] = el)}
                                settings={data?.application?.settings?.attachments}
                            />
                        );
                    }
                })}
            {/* <Attachments fileGroups={fileGroups} settings={settings?.attachments} /> */}
            <ApplicationActions
                handleCancel={() => handleOpenDialog("cancel")}
                canEdit={settings?.canEdit}
                canCancel={settings?.cancel?.can}
                canSubmit={settings?.submit?.can}
                settings={settings}
                handleSubmit={() => {
                    const errors = handleValidate({
                        requireEmailVerification: true,
                        forSubmit: true,
                    });

                    if (errors?.length > 0) {
                        setErrorBanner(
                            `<div>
                ${errors
                                ?.map((error, index) => `<p>${error}</p>`)
                                .join("")}
              </div>`
                        );
                    } else handleOpenDialog("submit");
                }}
                handleApprove={() => {
                    setOpenPreApproval(true);
                }}
                handleForApproval={() => {
                    handleOpenDialog("forApproval");
                }}
                handleSave={async () => {
                    const errors = handleValidate();

                    if (errors?.length > 0) {
                        setErrorBanner(
                            `<div>
                ${errors
                                ?.map((error, index) => `<p>${error}</p>`)
                                .join("")}
              </div>`
                        );
                    } else await handleSave();
                }}
                handleApproveSave={() => handleApproveSave()}
                handleReject={() => handleOpenDialog("reject")}
                handlePartiallyApprove={() => handleOpenDialog("partiallyApprove")}
                handleAmendment={() => setOpenAmendment(true)}
                handleBackToHome={() => handleOpenDialog("backToHome")}
                handleForResubmit={() => handleOpenDialog("forResubmit")}
                handleChangeIbans={() => setOpenChangeIban(true)}
            />
            <AlertBanner
                openInfo={!!errorBanner}
                setOpenMessage={setErrorBanner}
                info={{
                    message: `<div>${errorBanner}</div>`,
                    cssClass: "error",
                }}
            />
            {/* <Button onClick={() => handleSave()} variant="contained" color="primary">
        Log Form Values
      </Button> */}
            <History
                history={data?.application?.history}
                handlePreviewFile={handlePreviewFile}
            />
            <Amendment
                open={openAmendment}
                handleClose={() => setOpenAmendment(false)}
            />
            <ChangeChildrenIbansDialog
                open={openChangeIban}
                members={data?.application?.settings?.changeIbans?.members}
                onClose={() => setOpenChangeIban(false)}
                onSave={handleChangeIbansSave}
                initialIbans={data?.application?.childrenIbans}
            />
        </FormProvider>
    );
};

export default PanelladikesApplicationDetails;
